import React, { Component } from 'react'
import { alphanumericCompare } from '../../../services/utils/sort-util'
import { SAT_DOMAIN_LESSONS_MATH, SAT_DOMAIN_LESSONS_RW } from '../../../components/TestAnalysis/SATDomainLessons'
import { IDomainLesson, ISATDomainInfo, ISATDomainInfoMath, ISATDomainInfoRW, ISATSubDomainMap, ITopicsOverTimeRawData } from './types'
import { Column, RowData } from '../../../components/DataGrid/types/types'
import DataGrid from '../../../components/DataGrid'
import GrowthIndicator from './GrowthIndicator'
import { getTestDisplayName } from '../../../services/utils/practice-test-util'


interface Props {
    testSectionsRawData: any
    lessons: any
    compareLastTwo: boolean
}

interface State { }

export default class SATTopicsOverTime extends Component<Props, State> {
    render() {
        let {testSectionsRawData, lessons, compareLastTwo} = this.props
        let processedData = processTopicsOverTimeRawdata(testSectionsRawData, lessons)
        let {satDomainInfoRW, satDomainInfoMath} = processedData

        return (
            <>
                <SATReadingAndWritingTopicsOverTimeView data={satDomainInfoRW} compareLastTwo={compareLastTwo} />
                <SATMathTopicsOverTimeView data={satDomainInfoMath} compareLastTwo={compareLastTwo} />
            </>
        )
    }
}

//--- Helpers ---

function processTopicsOverTimeRawdata(topicsOverTimeRawdata: ITopicsOverTimeRawData[], lessons): ISATDomainInfo {
    let readingAnswers = topicsOverTimeRawdata.filter(t => t.name === "readingAnswers").flatMap(t => t.topicsOverTime)
    let writingAndLanguageAnswers = topicsOverTimeRawdata.filter(t => t.name === "writingAndLanguageAnswers").flatMap(t => t.topicsOverTime)
    let mathNoCalculatorAnswers = topicsOverTimeRawdata.filter(t => t.name === "mathNoCalculatorAnswers").flatMap(t => t.topicsOverTime)
    let mathCalculatorAnswers = topicsOverTimeRawdata.filter(t => t.name === "mathCalculatorAnswers").flatMap(t => t.topicsOverTime)

    // New sections for SAT (circa 2024+)
    let readingAndWritingAnswers = [...readingAnswers, ...writingAndLanguageAnswers]
    let mathAnswers = [...mathNoCalculatorAnswers, ...mathCalculatorAnswers]
    let satDomainInfoRW = getSATReadingAndWritingDomainInfo(readingAndWritingAnswers, lessons)
    let satDomainInfoMath = getSATMathDomainInfo(mathAnswers, lessons)

    return {
        satDomainInfoRW,
        satDomainInfoMath,
    }
}

export function getSATReadingAndWritingDomainInfo(satReadingAndWritingAnswers, lessons): ISATDomainInfoRW {
    // R&W Domains
    return {
        craftAndStructure: getCraftAndStructure(satReadingAndWritingAnswers, lessons),
        informationAndIdeas: getInformationAndIdeas(satReadingAndWritingAnswers, lessons),
        expressionOfIdeas: getExpressionOfIdeas(satReadingAndWritingAnswers, lessons),
        standardEnglishConventions: getStandardEnglishConventions(satReadingAndWritingAnswers, lessons),
    }
}

export function getSATMathDomainInfo(satMathAnswers, lessons): ISATDomainInfoMath {
    // Math Domains
     return {
        algebra: getAlgebra(satMathAnswers, lessons),
        advancedMath: getAdvancedMath(satMathAnswers, lessons),
        geometryAndTrigonometry: getGeometryAndTrigonometry(satMathAnswers, lessons),
        problemSolvingAndDataAnalysis: getProblemSolvingAndDataAnalysis(satMathAnswers, lessons),
    }
}


//--- Reading & Writing Section ---

function getCraftAndStructure(readingAndWritingAnswers, lessons) {
    let lessonIds = SAT_DOMAIN_LESSONS_RW.craftAndStructure
    return processDomainAndLessonsToGetSubdomainMap(readingAndWritingAnswers, lessonIds, lessons)
}

function getInformationAndIdeas(readingAndWritingAnswers, lessons) {
    let lessonIds = SAT_DOMAIN_LESSONS_RW.informationAndIdeas
    return processDomainAndLessonsToGetSubdomainMap(readingAndWritingAnswers, lessonIds, lessons)
}

function getExpressionOfIdeas(readingAndWritingAnswers, lessons) {
    let lessonIds = SAT_DOMAIN_LESSONS_RW.expressionOfIdeas
    return processDomainAndLessonsToGetSubdomainMap(readingAndWritingAnswers, lessonIds, lessons)
}

function getStandardEnglishConventions(readingAndWritingAnswers, lessons) {
    let lessonIds = SAT_DOMAIN_LESSONS_RW.standardEnglishConventions
    return processDomainAndLessonsToGetSubdomainMap(readingAndWritingAnswers, lessonIds, lessons)
}

//--- Math Section ---

function getAlgebra(satMathAnswers, lessons) {
    let lessonIds = SAT_DOMAIN_LESSONS_MATH.algebra
    return processDomainAndLessonsToGetSubdomainMap(satMathAnswers, lessonIds, lessons)
}

function getAdvancedMath(satMathAnswers, lessons) {
    let lessonIds = SAT_DOMAIN_LESSONS_MATH.advancedMath
    return processDomainAndLessonsToGetSubdomainMap(satMathAnswers, lessonIds, lessons)
}

function getGeometryAndTrigonometry(satMathAnswers, lessons) {
    let lessonIds = SAT_DOMAIN_LESSONS_MATH.geometryAndTrigonometry
    return processDomainAndLessonsToGetSubdomainMap(satMathAnswers, lessonIds, lessons)
}

function getProblemSolvingAndDataAnalysis(satMathAnswers, lessons) {
    let lessonIds = SAT_DOMAIN_LESSONS_MATH.problemSolvingAndDataAnalysis
    return processDomainAndLessonsToGetSubdomainMap(satMathAnswers, lessonIds, lessons)
}

/**
 * Processes the section domain data and returns the SAT subdomain map.
 * 
 * @param sectionData 
 * @param lessonIds 
 * @param lessons 
 * @returns 
 */
function processDomainAndLessonsToGetSubdomainMap(sectionData, lessonIds, lessons): ISATSubDomainMap {
    let lessonsAndTestsMap = {}

    sectionData.forEach(entry => {
        if (entry.key) {
            let {lessonId, lessonName} = entry.key
            let {testName, testDisplayName, answersSubmittedAt} = entry

            if (!lessonIds.includes(lessonId)) {
                return
            }

            if (!lessonsAndTestsMap[lessonId]) {
                // Create the initial one for lessonId
                lessonsAndTestsMap[lessonId] = {}
            }

            if (!lessonsAndTestsMap[lessonId][testName]) {
                // Create the initial one for further processing tests
                lessonsAndTestsMap[lessonId][testName] = {
                    testName,
                    testDisplayName,
                    lessonId,
                    lessonName,
                    correct: 0,
                    incorrect: 0,
                    blank: 0,
                    total: 0,
                    accuracy: 0,
                    isNA: true,
                    answersSubmittedAt,
                }
            }
            let lesson = lessonsAndTestsMap[lessonId][testName]
            lesson.correct += entry.correctCount
            lesson.total += entry.total
            lesson.isNA = lesson.total === 0
            lesson.accuracy = lesson.total > 0 ? lesson.correct / lesson.total : 0
        }
    })

    return lessonsAndTestsMap
}


function  SATReadingAndWritingTopicsOverTimeView(props: {data: any, compareLastTwo: boolean}) {
    let {data, compareLastTwo} = props

    return (
        <div className="collapsable-card practicetests-domain-analysis">
            <div className="collapsable-card__header ">
                <div className="lesson-list-card-header purple">
                    <span className="ribbon purple">
                        <img alt="" src="/assets/images/icons/subjects/ico-subject-english.svg" width="28" height="27" />
                    </span>
                    <h3 className="tas-header--text">SAT Reading & Writing</h3>
                </div>
            </div>
            <div className="collapsable-card__body pt-2">
                <SubViewForTopicsOverTime
                    title="CRAFT AND STRUCTURE"
                    cssClass="lesson-table--english mb-4"
                    subviewLessonIds={SAT_DOMAIN_LESSONS_RW.craftAndStructure}
                    subviewData={data.craftAndStructure}
                    compareLastTwo={compareLastTwo}
                />

                <SubViewForTopicsOverTime
                    title="INFORMATION AND IDEAS"
                    cssClass="lesson-table--english mb-4"
                    subviewLessonIds={SAT_DOMAIN_LESSONS_RW.informationAndIdeas}
                    subviewData={data.informationAndIdeas}
                    compareLastTwo={compareLastTwo}
                />

                <SubViewForTopicsOverTime
                    title="EXPRESSION OF IDEAS"
                    cssClass="lesson-table--english mb-4"
                    subviewLessonIds={SAT_DOMAIN_LESSONS_RW.expressionOfIdeas}
                    subviewData={data.expressionOfIdeas}
                    compareLastTwo={compareLastTwo}
                />

                <SubViewForTopicsOverTime
                    title="STANDARD ENGLISH CONVENTIONS"
                    cssClass="lesson-table--english"
                    subviewLessonIds={SAT_DOMAIN_LESSONS_RW.standardEnglishConventions}
                    subviewData={data.standardEnglishConventions}
                    compareLastTwo={compareLastTwo}
                />
            </div>
        </div>
    )
}


function  SATMathTopicsOverTimeView(props: {data: any, compareLastTwo: boolean}) {
    let {data, compareLastTwo} = props

    return (
        <div className="collapsable-card practicetests-domain-analysis">
            <div className="collapsable-card__header ">
                <div className="lesson-list-card-header blue">
                    <span className="ribbon blue">
                        <img alt="" src="/assets/images/icons/subjects/ico-subject-math.svg" width="28" height="27" />
                    </span>
                    <h3 className="tas-header--text">SAT Math</h3>
                </div>
            </div>
            <div className="collapsable-card__body">
                <SubViewForTopicsOverTime
                    title="ALGEBRA"
                    subviewLessonIds={SAT_DOMAIN_LESSONS_MATH.algebra}
                    cssClass="lesson-table--math mb-4"
                    subviewData={data.algebra}
                    compareLastTwo={compareLastTwo}
                />

                <SubViewForTopicsOverTime
                    title="ADVANCED MATH"
                    subviewLessonIds={SAT_DOMAIN_LESSONS_MATH.advancedMath}
                    cssClass="lesson-table--math mb-4"
                    subviewData={data.advancedMath}
                    compareLastTwo={compareLastTwo}
                />

                <SubViewForTopicsOverTime
                    title="GEOMETRY & TRIGONOMETRY"
                    subviewLessonIds={SAT_DOMAIN_LESSONS_MATH.geometryAndTrigonometry}
                    cssClass="lesson-table--math mb-4"
                    subviewData={data.geometryAndTrigonometry}
                    compareLastTwo={compareLastTwo}
                />

                <SubViewForTopicsOverTime
                    title="PROBLEM-SOLVING AND DATA ANALYSIS"
                    subviewLessonIds={SAT_DOMAIN_LESSONS_MATH.problemSolvingAndDataAnalysis}
                    cssClass="lesson-table--math"
                    subviewData={data.problemSolvingAndDataAnalysis}
                    compareLastTwo={compareLastTwo}
                />
            </div>
        </div>
    )
}

function getDomainLessons(subviewData): IDomainLesson[] {
    let lessonsIds = Object.keys(subviewData)
    let lessons: IDomainLesson[] = []
    lessonsIds.forEach(lessonId => {
        let testsMap = subviewData[lessonId]
        let testNames = Object.keys(testsMap)
        let firstTest = testsMap[testNames[0]]
        let lessonName = testsMap ? firstTest.lessonName : "?"
        lessons.push({
            lessonId,
            lessonName,
            testsMap,
        })
    })

    return lessons
}

function SubViewForTopicsOverTime(props) {
    let {title, subviewLessonIds, subviewData, cssClass, compareLastTwo} = props

    return (
        <div className={`subcategory-lesson-list ${cssClass}`}>
            <h5 className="h6 subcategory-title mt-0">{title}</h5>
            <LessonByTestView subviewData={subviewData} compareLastTwo={compareLastTwo} />
        </div>
    )
}


let sortMethod = alphanumericCompare
let columns: Column[] = []

/**
 * Sets up the columns dynamically based on the SAT tests taken by the student.
 * 
 * @param domainLessons 
 */
function setupColumns(domainLessons: IDomainLesson[]) {
    let testsMap = domainLessons.map(lesson => lesson.testsMap)
    let testNamesMap = {}

    testsMap.forEach(tm => {
        let testNames = Object.keys(tm)
        testNamesMap = { 
            ...testNamesMap, 
            ...testNames.reduce((acc, testName) => ({ ...acc, [testName]: true }), {}) 
        }
    })

    let dynamicColumns: Column[] = [
        {
            title: "LESSON",
            dataProperty: "lessonName",
            width: "35%",
            sortable: true,
            sortMethod,
        },
        {
            title: "GROWTH",
            dataProperty: "growth",
            width: "8%",
            sortable: false,
	    }
    ]

    // Add columns dynamically (based on the # of tests)
    let testNames = Object.keys(testNamesMap)
    testNames.forEach(testName=> {
        let testDisplayName = getTestDisplayName(testName).toUpperCase()
        let col = {
            title: testDisplayName,
            dataProperty: testName,
            sortable: true,
            sortMethod,
            width:'8%',
        }
        dynamicColumns.push(col)
    })

    // Set columns
    columns = dynamicColumns
}

/**
 * Prepares the data grid info for the topics over time.
 * 
 * @param domainLessonWithTestsData 
 * @param idx 
 * @param compareLastTwo 
 * @returns 
 */
function createGridData(domainLessonWithTestsData: any, idx: number, compareLastTwo: boolean): RowData {
    if (!domainLessonWithTestsData) return { id: "?", data: {}}

    function getTestData(testName: string) {
        return domainLessonWithTestsData.testsMap[testName]
    }

    function getTestInfoView(testData) {
        let testInfo = testData
        let testInfoNA = {isNA: true, }
        let {isNA, correct, total, accuracy} = testInfo ? testInfo: testInfoNA
        let accuracyInfo = `${Math.round(100*accuracy)}%`

        return () => <div>
            {isNA ?
                "N/A"
                :
                <>
                    {correct}/{total}
                    <br/>
                    <b>{accuracyInfo}</b>
                </>
            }
        </div>
    }

    /**
     * Finds two tests for growth comparison
     * @returns 
     */
    function findComparedTests() {
        let testsMap = domainLessonWithTestsData.testsMap
        let tests = Object.keys(testsMap).map(testName => testsMap[testName]).filter(t => t.total > 0)
        let sortedTests = tests.sort((t1, t2) => t1.answersSubmittedAt - t2.answersSubmittedAt)
        let index1 = 0
        let index2 = 0
        let nofTests = sortedTests.length

        if (compareLastTwo) {
            index1 = nofTests - 2
            index2 = nofTests - 1
        }
        else {
            index1 = 0
            index2 = nofTests - 1
        }

        if (nofTests == 1) {
            // There is no other test to compare to!
            index2 = -1
        }

        let firstTest = sortedTests[index1]
        let secondTest = sortedTests[index2]

        return { firstTest, secondTest }
    }

    function getGrowthInfo() {
        let {firstTest, secondTest} = findComparedTests()
        let valuesForNA = {correctCount: 0, total : 0}
        let before = firstTest ? {correctCount: firstTest.correct, total: firstTest.total}: valuesForNA
        let after = secondTest ? {correctCount: secondTest.correct, total: secondTest.total}: valuesForNA

        return () => 
            <GrowthIndicator
                before={before}
                after={after}
            />
    }

    let dataDiagnostic = getTestData("DiagnosticSAT")
    let dataTest1 = getTestData("SAT1")
    let dataTest2 = getTestData("SAT2")
    let dataTest3 = getTestData("SAT3")
    let dataTest4 = getTestData("SAT4")
    let dataTest5 = getTestData("SAT5")
    let comporatorD = dataDiagnostic ? dataDiagnostic.correct: -1
    let comporator1 = dataTest1 ? dataTest1.correct: -1
    let comporator2 = dataTest2 ? dataTest2.correct: -1
    let comporator3 = dataTest3 ? dataTest3.correct: -1
    let comporator4 = dataTest4 ? dataTest4.correct: -1
    let comporator5 = dataTest5 ? dataTest5.correct: -1

    let lessonByTestsData = {
        lessonName: {
            content: domainLessonWithTestsData.lessonName,
            comparator: domainLessonWithTestsData.lessonName
        },
        DiagnosticSAT: {
            content: getTestInfoView(dataDiagnostic),
            comparator: comporatorD,
        },
        SAT1: {
            content: getTestInfoView(dataTest1),
            comparator: comporator1,
        },
        SAT2: {
            content: getTestInfoView(dataTest2),
            comparator: comporator2,
        },
        SAT3: {
            content: getTestInfoView(dataTest3),
            comparator: comporator3,
        },
        SAT4: {
            content: getTestInfoView(dataTest4),
            comparator: comporator4,
        },
        SAT5: {
            content: getTestInfoView(dataTest5),
            comparator: comporator5,
        },
        growth: {
            content: getGrowthInfo(),
            comparator: -1,
        }
    };

    return {
        data: lessonByTestsData,
        id: `sat-topic-over-time-data${idx}`
    };
}

function LessonByTestView(props) {
    let {subviewData, compareLastTwo} = props
    let domainLessons = getDomainLessons(subviewData)
    let dataGridClass = "test-table first-table"
    let data = domainLessons.map((domainLesson, idx) => createGridData(domainLesson, idx, compareLastTwo))

    // Make sure to setup the colums for the datagrid!
    setupColumns(domainLessons)

    return ( 
        <div>
            <DataGrid
                columns={columns}
                data={data}
                className={dataGridClass}
            />
        </div>
    )
}
